import { useMemo } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { DynamicProductBlockQuotation } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../../../hooks/use-path-locale';
import { SystemPageLink } from '../../../../system-page-link/system-page-link';

import { QuoteFlyoutQuotationLine } from './quote-flyout-quotation-line';

interface Props {
   quotation: DynamicProductBlockQuotation;
   quoteDetailRoute: string;
}

export const QuoteFlyoutQuotation = ({ quotation, quoteDetailRoute }: Props) => {
   const translate = useTranslations();
   const format = useFormatter();
   const locale = usePathLocale();

   const sortedQuotationLines = useMemo(() => quotation.lines.sort((a, b) => a.quantity.amount - b.quantity.amount), [quotation]);

   const productReference = useMemo(() => {
      const references = new Set(quotation.lines.map((line) => line.customerProductReference).filter((cpref) => cpref));
      return references.size === 1 ? [...references][0] : undefined;
   }, [quotation]);

   return (
      <Box direction="column">
         <Divider />
         <Box justify="between">
            <Typography component="h4" variant="body-highlight">
               {quotation.name}
            </Typography>
            <Box justify="end">
               <SystemPageLink
                  baseRoute={quoteDetailRoute}
                  type={SystemPageType.QUOTE_DETAIL}
                  locale={locale}
                  slug={quotation.id}
                  target="_blank"
                  underline="always"
               >
                  {translate('quote-flyout.view-all-details')}
               </SystemPageLink>
            </Box>
         </Box>
         <Box direction="column" gap="2xs">
            <Typography component="span" variant="body-highlight">
               {translate('quote-flyout.general-product-comment')}
            </Typography>
            {quotation.description ? (
               <Typography component="span" variant="body">
                  {quotation.description}
               </Typography>
            ) : (
               <Typography color="weak">{translate('quote-flyout.no-general-comments-found')}</Typography>
            )}
         </Box>
         {sortedQuotationLines.map((line) => (
            <QuoteFlyoutQuotationLine key={line.id} quotationLine={line} />
         ))}
         <Box direction="column" gap="2xs">
            <Typography component="span" variant="body-highlight">
               {translate('quote-flyout.validity-label')}
            </Typography>
            <Typography component="span" variant="body">
               {format.dateTime(quotation.effective.from)} - {format.dateTime(quotation.effective.until)}
            </Typography>
         </Box>
         {productReference && (
            <Box direction="column" gap="2xs">
               <Typography variant="body-highlight">{translate('quote-flyout.product-reference-label')}</Typography>
               <Typography component="span" variant="body">
                  {productReference}
               </Typography>
            </Box>
         )}
      </Box>
   );
};
